import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import Button from '../../atoms/Button';
import StatusChip from '../../atoms/StatusChip';
import MetaPill from '../../atoms/MetaPill';
import CoverArt, { pickCoverVariant } from '../../atoms/CoverArt';
import {
    mapEventToStatusVariant,
    mapViewerStatusToVariant,
    resolveStatusLabel,
    resolveViewerStatusLabel,
    buildShortLocationLabel,
    formatShortScheduledLabel,
    resolveEventHostLabel,
} from '../../../utils/eventPresentation';
import './index.scss';

export default function CalendarNextEventCard({
    event,
    onOpen,
    onRsvp,
    ctaAction = 'rsvp',
    ctaLabel,
    eyebrowLabel,
    emptyLabel,
    isRsvpSubmitting = false,
    className = '',
}) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language || 'en-US';

    const title = event?.title || '';
    const host = event ? resolveEventHostLabel(t, event) : '';
    const statusVariant = mapEventToStatusVariant(event?.status);
    const viewerVariant = mapViewerStatusToVariant(event?.viewerAttendanceStatus);
    const statusLabel = resolveStatusLabel(t, event?.status);
    const viewerLabel = resolveViewerStatusLabel(t, event?.viewerAttendanceStatus);
    const coverVariant = pickCoverVariant(event?.id || event?.title);
    const shortLocation = buildShortLocationLabel(event?.location);

    const scheduledLabel = useMemo(
        () => formatShortScheduledLabel(locale, event?.startAt, event?.timezone),
        [event?.startAt, event?.timezone, locale],
    );

    const handleHeaderClick = () => {
        if (typeof onOpen === 'function' && event) {
            onOpen(event);
        }
    };

    return (
        <section className={classNames('calendar-next-event', className)}>
            <h4 className="calendar-next-event__eyebrow">
                {eyebrowLabel || t('eventsPage.calendar.sideRail.nextEvent')}
            </h4>
            {event ? (
                <>
                    <button
                        type="button"
                        className="calendar-next-event__hero-button"
                        onClick={handleHeaderClick}
                        aria-label={title}
                    >
                        <CoverArt className="calendar-next-event__hero" variant={coverVariant}>
                            <span className="calendar-next-event__hero-overlay" aria-hidden="true" />
                            <div className="calendar-next-event__hero-chips">
                                <StatusChip variant={statusVariant}>{statusLabel}</StatusChip>
                                {viewerLabel ? (
                                    <StatusChip variant={viewerVariant}>{viewerLabel}</StatusChip>
                                ) : null}
                            </div>
                        </CoverArt>
                    </button>
                    <h3 className="calendar-next-event__title">{title}</h3>
                    {host ? (
                        <p className="calendar-next-event__host">
                            {t('eventsPage.card.hostedBy')} <strong>{host}</strong>
                        </p>
                    ) : null}
                    <div className="calendar-next-event__meta">
                        {scheduledLabel ? <MetaPill>{scheduledLabel}</MetaPill> : null}
                        {shortLocation ? <MetaPill>{shortLocation}</MetaPill> : null}
                    </div>
                    <div className="calendar-next-event__actions">
                        <Button
                            type="button"
                            variant="gradient"
                            size="small"
                            shape="pill"
                            disabled={isRsvpSubmitting}
                            onClick={() => {
                                if (ctaAction === 'rsvp' && typeof onRsvp === 'function') {
                                    onRsvp(event);
                                } else {
                                    handleHeaderClick();
                                }
                            }}
                            className="calendar-next-event__cta"
                        >
                            {ctaLabel || t('eventsPage.actions.iGoing')}
                        </Button>
                    </div>
                </>
            ) : (
                <p className="calendar-next-event__empty">
                    {emptyLabel || t('eventsPage.calendar.empty.month')}
                </p>
            )}
        </section>
    );
}

CalendarNextEventCard.propTypes = {
    event: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        status: PropTypes.string,
        startAt: PropTypes.string,
        timezone: PropTypes.string,
        viewerAttendanceStatus: PropTypes.string,
        author: PropTypes.shape({
            displayName: PropTypes.string,
            username: PropTypes.string,
        }),
        organizer: PropTypes.shape({
            objectType: PropTypes.string,
            objectId: PropTypes.string,
            name: PropTypes.string,
        }),
    }),
    onOpen: PropTypes.func,
    onRsvp: PropTypes.func,
    ctaAction: PropTypes.oneOf(['open', 'rsvp']),
    ctaLabel: PropTypes.string,
    eyebrowLabel: PropTypes.string,
    emptyLabel: PropTypes.string,
    isRsvpSubmitting: PropTypes.bool,
    className: PropTypes.string,
};
